import { Bold, Braces, Code, Italic, Link2, List, ListOrdered } from 'lucide-react';
import type { Editor } from '@tiptap/react';
import type { Attachment } from '@rundown/types';
import { cx } from '../../lib/cx';
import { AttachButton } from './attachments';
import { prettyJson } from './highlighting';

const button = (label: string, icon: React.ReactNode, isActive: boolean, run: () => void) => (
  <button
    key={label}
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={isActive}
    // Toolbars steal focus from the document, and a command applied to a
    // collapsed selection that has just been blurred applies to nothing.
    onMouseDown={(event) => event.preventDefault()}
    onClick={run}
    className={cx(
      'rounded-md p-1.5 hover:bg-surface-3',
      isActive ? 'bg-surface-3 text-ink' : 'text-muted hover:text-ink',
    )}
  >
    {icon}
  </button>
);

/**
 * The formatting controls above an editor.
 *
 * ── Six controls, and no more ───────────────────────────────────────────────
 * Bold, italic, code, two lists, a link, and a code block. That is the whole
 * allowlist the server validates minus mentions, so there is nothing offered
 * here that saving would reject, and nothing storable that cannot be reached.
 * Markdown input rules come with StarterKit: `- ` makes a list, ``` makes a
 * code block, so the toolbar is a discovery aid rather than the only way in.
 *
 * Shown on the description and hidden in the thread by default — a reply is
 * usually a sentence, and a formatting bar over every one of them is furniture.
 * The keyboard shortcuts and input rules work in both regardless.
 */
export function Toolbar({
  editor,
  onUpload,
}: {
  editor: Editor;
  onUpload?: (files: File[], destination: 'inline' | 'attach') => Promise<readonly Attachment[]>;
}) {
  /**
   * Prettify, offered only when there is something to prettify.
   *
   * Shown when the cursor is in a code block whose contents are JSON that is
   * not already formatted this way — so it never appears as a button that
   * visibly does nothing, and never reformats something the author laid out
   * deliberately without being asked.
   */
  const inCode = editor.isActive('codeBlock');
  const codeText = inCode ? editor.state.selection.$from.parent.textContent : '';
  const pretty = inCode ? prettyJson(codeText) : null;

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-line px-2 py-1.5">
      {button('Bold', <Bold size={14} />, editor.isActive('bold'), () =>
        editor.chain().focus().toggleBold().run(),
      )}
      {button('Italic', <Italic size={14} />, editor.isActive('italic'), () =>
        editor.chain().focus().toggleItalic().run(),
      )}
      {button('Inline code', <Code size={14} />, editor.isActive('code'), () =>
        editor.chain().focus().toggleCode().run(),
      )}
      <span className="mx-1 h-4 w-px bg-line" aria-hidden />
      {button('Bulleted list', <List size={14} />, editor.isActive('bulletList'), () =>
        editor.chain().focus().toggleBulletList().run(),
      )}
      {button('Numbered list', <ListOrdered size={14} />, editor.isActive('orderedList'), () =>
        editor.chain().focus().toggleOrderedList().run(),
      )}
      {button('Code block', <Braces size={14} />, inCode, () =>
        editor.chain().focus().toggleCodeBlock().run(),
      )}
      {/* Always attaches, images included — see attachments.tsx. Pasting is
          how you put a screenshot IN the text; this is how you put one on the
          record without it. */}
      {onUpload !== undefined && (
        <AttachButton onFiles={(files) => void onUpload(files, 'attach')} label="Attach a file" />
      )}
      {button('Link', <Link2 size={14} />, editor.isActive('link'), () => {
        const previous: unknown = editor.getAttributes('link')['href'];
        const href = window.prompt('Link to', typeof previous === 'string' ? previous : 'https://');
        if (href === null) return;
        if (href.trim() === '') {
          editor.chain().focus().unsetLink().run();
          return;
        }
        editor.chain().focus().setLink({ href: href.trim() }).run();
      })}

      {pretty !== null && (
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            const { $from } = editor.state.selection;
            const start = $from.start();
            editor
              .chain()
              .focus()
              .insertContentAt({ from: start, to: start + $from.parent.content.size }, pretty)
              .run();
          }}
          className="ml-auto rounded-md px-2 py-1 fs-11 font-semibold text-accent hover:bg-accent-tint"
        >
          Prettify JSON
        </button>
      )}
    </div>
  );
}
